import React from 'react';

import { useT } from 'client/i18n/hooks/useT';
import { useCurrentUserCtx } from 'contexts/CurrentUser';
import { DropdownMenuContainer } from 'components/DropdownMenu/DropdownMenuContainer';
import Text from 'shared/Text';
import Box from 'shared/Box';
import Img from 'components/Img';
import { paths } from 'client/shared/Paths';
import { usePartnerIds } from 'client/hooks/user/usePartnerIds';
import { LanguageSelectionModal } from 'client/components/LanguageSelector/LanguageSelectionModal';
import {
  StyledCaret,
  StyledCoverArtBox,
  SettingsMenuBox,
  StyledSettingsSpan,
} from './styles';
import { StyledNavLink } from '../../LinkStyles';
import {
  clickedNavSettingsButton,
  clickedNavLanguageButton,
} from '../../events';

const defaultPodcastImage =
  'https://d12xoj7p9moygp.cloudfront.net/images/default-podcast-image.png';
const coverArtPlaceholder =
  'https://d12xoj7p9moygp.cloudfront.net/images/avatars/cover_art_placeholder.png';

type AccountNavItemsProps = {
  onLogOut: (event: Event) => void;
  podcastName?: string | null;
  podcastImage?: string | null;
  showNetworkLinks: boolean;
  isSaiOrVodcast: boolean;
};

const isShowingCoverArt = (podcastImage: string | null) =>
  podcastImage && podcastImage !== defaultPodcastImage;
const renderCoverArt = (podcastImage: string | null) => (
  <StyledCoverArtBox marginRight={10} minWidth={28} overflow="hidden">
    <Img
      height={28}
      width={28}
      src={podcastImage || coverArtPlaceholder}
      alt="Podcast cover art"
    />
  </StyledCoverArtBox>
);

export const AccountNavItems = ({
  onLogOut,
  podcastName = null,
  podcastImage = null,
  showNetworkLinks,
  isSaiOrVodcast,
}: AccountNavItemsProps) => {
  const { resetCurrentUser } = useCurrentUserCtx();
  const { resetPartnerIds } = usePartnerIds();
  const t = useT();

  const [
    isLanguageSelectionOpen,
    setIsLanguageSelectionOpen,
  ] = React.useState<boolean>(false);

  const ACCOUNT_MENU_ITEMS = [
    {
      type: 'link',
      label: t(
        'PublicWebsiteNav.AccountNavItems.UpdateSettings',
        'Update settings',
        'Link to "Update settings" web page'
      ),
      icon: {
        type: 'GearIcon',
        iconColor: '#c9cbcd',
        backgroundColor: 'transparent',
        width: 18,
        padding: 0,
      },
      to: '/dashboard/podcast/edit',
    },
    {
      type: 'item',
      label: t(
        'PublicWebsiteNav.AccountNavItems.Language',
        'Language',
        'Link to Language selection modal'
      ),
      icon: {
        type: 'GlobeIcon',
        iconColor: '#c9cbcd',
        backgroundColor: 'transparent',
        width: 18,
        padding: 0,
      },
      onClick: () => {
        clickedNavLanguageButton();
        setIsLanguageSelectionOpen(true);
      },
    },
    {
      type: 'link',
      label: t(
        'PublicWebsiteNav.AccountNavItems.Help',
        'Help',
        'Link to "Help" web page'
      ),
      icon: {
        type: 'QuestionMarkIcon',
        iconColor: '#ffffff',
        backgroundColor: 'gray',
        width: 18,
        padding: 3,
      },
      target: '_blank',
      href: paths.getHelpPage('home'),
    },
    {
      type: 'item',
      label: t(
        'PublicWebsiteNav.AccountNavItems.LogOut',
        'Log out',
        'Link to "Log out" web page'
      ),
      icon: {
        type: 'PowerIcon',
        iconColor: '#c9cbcd',
        backgroundColor: 'transparent',
        width: 18,
        padding: 0,
      },
      onClick: (event: Event) => {
        onLogOut(event);
        resetCurrentUser();
        resetPartnerIds();
      },
    },
  ];

  const isShowingPodcastCoverOrName =
    isShowingCoverArt(podcastImage) && podcastName;

  return (
    <>
      <DropdownMenuContainer
        shouldShowDistributionItem={true}
        alignFlyout="bottom"
        flyoutMarginTop={4}
        renderPressableContent={
          <SettingsMenuBox>
            {isShowingPodcastCoverOrName && renderCoverArt(podcastImage)}
            <StyledSettingsSpan onClick={clickedNavSettingsButton}>
              {t(
                'PublicWebsiteNav.AccountNavItems.Settings',
                'Settings',
                'Link to "Settings" web page'
              )}
            </StyledSettingsSpan>
            <StyledCaret />
          </SettingsMenuBox>
        }
        renderMenuHeader={
          isShowingPodcastCoverOrName ? (
            <Box display="flex" alignItems="center">
              {isShowingCoverArt(podcastImage) && renderCoverArt(podcastImage)}
              <Text color="#292f36" isBold size="md" isInline>
                {(podcastName || '').slice(0, 57)}
                {(podcastName || '').length > 57 ? '…' : ''}
              </Text>
            </Box>
          ) : null
        }
        renderMenuFooter={
          showNetworkLinks && !isSaiOrVodcast ? (
            <StyledNavLink
              className=""
              href=""
              isDownloadLink={false}
              to="/dashboard/podcastnetwork"
              fontSize={18}
            >
              <Box
                width="100%"
                height={48}
                paddingLeft={14}
                paddingRight={14}
                display="flex"
                alignItems="center"
              >
                <Text color="#292f36" isBold size="xl" isInline>
                  {t(
                    'PublicWebsiteNav.AccountNavItems.PodcastNetwork',
                    'Podcast Network',
                    'Link to "Podcast Network" web page'
                  )}
                </Text>
              </Box>
            </StyledNavLink>
          ) : null
        }
        menuItems={ACCOUNT_MENU_ITEMS}
      />
      {isLanguageSelectionOpen && (
        <LanguageSelectionModal
          onClose={() => setIsLanguageSelectionOpen(false)}
        />
      )}
    </>
  );
};
